<script setup lang="ts">
import type { UIInputBaseProps } from '../../types'
import type { MaskInputOptions } from 'maska'
import { ref, computed, watch, useToggle } from '#imports'
import { UIInput, UIPasswordStrength } from '#components'

export interface UIPasswordInputProps extends UIInputBaseProps {
    /** Id of the input, also used by its label. Generated when omitted. */
    id?: string
    // Label
    // Mask
    mask?: MaskInputOptions
    showPasswordStrength?: boolean
    /**
     * Show compact (abbreviated) password requirements instead of full messages.
     * When true, displays inline labels like "8+", "A-Z", "a-z", "0-9".
     * Automatically expands to full-size when validation errors occur.
     */
    compactStrength?: boolean
    /**
     * Allow toggling between password input and regular text input
     */
    toggleType?: boolean
    autofocus?: boolean
}

const props = withDefaults(defineProps<UIPasswordInputProps>(), {
    modelValue: undefined,
    mask: undefined,
    autocomplete: 'on',
    showPasswordStrength: true,
    compactStrength: false,
    toggleType: true,
    autofocus: false,
})

const modelValue = defineModel<string>()
const [showPassword, toggleShowPassword] = useToggle(false)

const hasFocusedInput = ref(false)
const hasHadError = ref(false)

const showStrength = computed(() => props.showPasswordStrength && hasFocusedInput.value)
const isCompact = computed(() => props.compactStrength && !hasHadError.value)

function handleInputFocus(isFocused: boolean) {
    if (!isFocused) return
    if (hasFocusedInput.value) return
    if (!props.showPasswordStrength) return

    hasFocusedInput.value = true
}

const inputRef = ref<InstanceType<typeof UIInput>>()

// Watch for validation errors to expand compact strength indicator
watch(() => inputRef.value?.context?.meta?.validated && !inputRef.value?.context?.meta?.valid, (hasError) => {
    if (hasError) {
        hasHadError.value = true
    }
})
</script>

<template>
    <div class="password-input">
        <UIInput
            ref="inputRef"
            v-bind="props"
            v-model="modelValue"
            :type="showPassword ? 'text' : 'password'"
            :autofocus="props.autofocus"
            @focus="handleInputFocus"
        >
            <!-- Expose all slots of the third-party component -->
            <template v-for="(_, name) in $slots" #[name]="slotProps">
                <slot :name="name" v-bind="slotProps || {}" />
            </template>

            <template v-if="toggleType" #append>
                <UIButton
                    icon
                    tabindex="-1"
                    type="button"
                    @click.prevent="toggleShowPassword()"
                >
                    <UIIcon
                        name="eye"
                        :color="showPassword ? 'primary' : 'var(--password-eye-color, grey)'"
                        size="22px"
                    />
                </UIButton>
            </template>
        </UIInput>

        <Transition name="slide-up-inline">
            <UIPasswordStrength
                v-if="showStrength"
                :password="modelValue ?? ''"
                :compact="isCompact"
                class="password-input__strength"
            />
        </Transition>
    </div>
</template>

<style lang="scss" scoped>
.password-input {
    &__strength {
        margin-top: 10px;
    }
}
</style>
